import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

function ActivityLog() {
  const { t, lang } = useLanguage();
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';
  const [logDates, setLogDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datesLoading, setDatesLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchLogDates = useCallback(async () => {
    setDatesLoading(true);
    try {
      const res = await axios.get('/api/admin/logs/dates');
      setLogDates(res.data || []);
    } catch {
      setError(t('activityLog.errorLoadingDates'));
    } finally {
      setDatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogDates();
  }, [fetchLogDates]);

  const fetchLogs = useCallback(async (date) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/admin/logs', { params: { date } });
      setLogs(res.data || []);
    } catch {
      setError(t('activityLog.errorLoadingLogs'));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDayClick = (dateStr) => {
    if (!logDates.includes(dateStr)) return;
    setSelectedDate(dateStr);
    fetchLogs(dateStr);
  };

  const handleClearAll = async () => {
    setConfirmDialog(null);
    try {
      await axios.delete('/api/admin/logs');
      setLogs([]);
      setLogDates([]);
      setSelectedDate(null);
    } catch {
      setError(t('activityLog.errorClearAll'));
    }
  };

  const handleDeleteDay = async (date) => {
    setConfirmDialog(null);
    try {
      await axios.delete(`/api/admin/logs/date/${date}`);
      const updated = logDates.filter(d => d !== date);
      setLogDates(updated);
      if (selectedDate === date) {
        setSelectedDate(null);
        setLogs([]);
      }
    } catch {
      setError(t('activityLog.errorDeleteDay'));
    }
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const formatDateStr = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Jan 1 2024 is a Monday — used to generate ordered weekday labels
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleString(locale, { weekday: 'short' })
  );

  const { year, month } = calendarMonth;
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  // Adjust so week starts on Monday (0=Mo...6=So)
  const firstDayMon = (firstDay + 6) % 7;

  const calendarCells = [];
  for (let i = 0; i < firstDayMon; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const getActionColor = (action) => {
    if (action.includes('gelöscht') || action.includes('Lösch')) return 'var(--error, #ef4444)';
    if (action.includes('erstellt') || action.includes('generiert')) return 'var(--success, #22c55e)';
    if (action.includes('Login')) return 'var(--gold, #f59e0b)';
    if (action.includes('bearbeitet') || action.includes('Ergebnis')) return 'var(--info, #3b82f6)';
    return 'var(--text-secondary, #6b7280)';
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{t('activityLog.title')}</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>
            {t('activityLog.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setConfirmDialog({ type: 'all' })}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: '1px solid var(--error, #ef4444)',
            color: 'var(--error, #ef4444)',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t('activityLog.clearAll')}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 320px) 1fr', gap: 20, alignItems: 'start' }}>
        {/* Calendar */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={prevMonth} style={navBtnStyle}>&#8249;</button>
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              {new Date(year, month, 1).toLocaleString(locale, { month: 'long' })} {year}
            </span>
            <button onClick={nextMonth} style={navBtnStyle}>&#8250;</button>
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {weekdayLabels.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary, #9ca3af)', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Calendar days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {calendarCells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;
              const dateStr = formatDateStr(year, month, day);
              const hasLogs = logDates.includes(dateStr);
              const isSelected = selectedDate === dateStr;

              return (
                <button
                  key={dateStr}
                  onClick={() => handleDayClick(dateStr)}
                  title={hasLogs ? t('activityLog.hasLogs', { day }) : `${day}.`}
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1',
                    border: isSelected ? '2px solid var(--gold, #f59e0b)' : '1px solid transparent',
                    borderRadius: 6,
                    background: isSelected
                      ? 'rgba(245,158,11,0.15)'
                      : hasLogs
                        ? 'rgba(59,130,246,0.08)'
                        : 'transparent',
                    cursor: hasLogs ? 'pointer' : 'default',
                    fontSize: 13,
                    fontWeight: hasLogs ? 600 : 400,
                    color: hasLogs ? 'var(--text-primary, inherit)' : 'var(--text-secondary, #9ca3af)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    padding: 0,
                  }}
                >
                  {day}
                  {hasLogs && (
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold, #f59e0b)' }} />
                  )}
                </button>
              );
            })}
          </div>

          {datesLoading && (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginTop: 8 }}>{t('common.loading')}</p>
          )}

          {logDates.length === 0 && !datesLoading && (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginTop: 12 }}>
              {t('activityLog.noLogsYet')}
            </p>
          )}

          {/* Legend */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-color, #e5e7eb)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold, #f59e0b)', display: 'inline-block' }} />
              {t('activityLog.logsPresent')}
            </div>
          </div>
        </div>

        {/* Log List */}
        <div>
          {selectedDate ? (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                    {formatDisplayDate(selectedDate)}
                  </h2>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {logs.length} {logs.length === 1 ? t('activityLog.entry') : t('activityLog.entries')}
                  </p>
                </div>
                <button
                  onClick={() => setConfirmDialog({ type: 'day', date: selectedDate })}
                  style={{
                    padding: '6px 12px',
                    background: 'transparent',
                    border: '1px solid var(--error, #ef4444)',
                    color: 'var(--error, #ef4444)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {t('activityLog.deleteDay')}
                </button>
              </div>

              {loading ? (
                <p style={{ textAlign: 'center', padding: 20 }}>{t('common.loading')}</p>
              ) : logs.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>{t('activityLog.noLogs')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {logs.map(log => (
                    <div
                      key={log.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '145px 1fr',
                        gap: '8px 16px',
                        padding: '10px 14px',
                        borderRadius: 8,
                        background: 'var(--bg-secondary, rgba(0,0,0,0.02))',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        alignItems: 'start',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: getActionColor(log.action),
                            padding: '2px 8px',
                            borderRadius: 4,
                            background: `${getActionColor(log.action)}18`,
                            border: `1px solid ${getActionColor(log.action)}30`,
                          }}>
                            {log.action}
                          </span>
                          {log.username && (
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                              {t('activityLog.by')} <strong>{log.username}</strong>
                            </span>
                          )}
                        </div>
                        {log.details && (
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                            {log.details}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p style={{ margin: 0, fontSize: 15 }}>{t('activityLog.selectDay')}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13 }}>{t('activityLog.selectDayHint')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ padding: 24, maxWidth: 380, width: '90%' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('tournamentDelete.confirm')}</h3>
            <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: 14 }}>
              {confirmDialog.type === 'all'
                ? t('activityLog.confirmClearAll')
                : t('activityLog.confirmDeleteDay', { date: formatDisplayDate(confirmDialog.date) })}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDialog(null)}
                style={{ padding: '8px 16px', border: '1px solid var(--border-color, #e5e7eb)', background: 'transparent', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => confirmDialog.type === 'all' ? handleClearAll() : handleDeleteDay(confirmDialog.date)}
                style={{ padding: '8px 16px', border: 'none', background: 'var(--error, #ef4444)', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtnStyle = {
  width: 28,
  height: 28,
  border: '1px solid var(--border-color, #e5e7eb)',
  background: 'transparent',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-primary, inherit)',
};

export default ActivityLog;
